import {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'

interface TimerStateContextValue {
  /** Called by each TimeTracker when its isRunning state changes. */
  setTimerRunning: (fieldName: string, isRunning: boolean) => void
  /**
   * Stops (pauses + flushes) every running timer except the one named.
   * Call this right before starting a timer so only one runs at a time.
   */
  stopOtherTimers: (exceptFieldName: string) => void
  /** Stable ref — TimeTracker assigns its current pause fn here on each render. */
  pauseFunctionsRef: RefObject<Record<string, () => void>>
  /** Stable ref — TimeTracker assigns its current start fn here on each render. */
  startFunctionsRef: RefObject<Record<string, () => void>>
  /** Reactive map of fieldName → isRunning, consumed by TimeField. */
  runningTimers: Partial<Record<string, boolean>>
  /** Whether the "Other" time category is visible in both the panel and the header row. */
  showOtherTime: boolean
  setShowOtherTime: (show: boolean) => void
  /**
   * Stable ref — TimeWorkLogPanel registers a function here that opens the panel
   * with "Other" pre-selected. ElapsedTimeInput calls it from the "+ Other" button.
   */
  openTimePanelForOtherRef: RefObject<(() => void) | null>
  /** Stable ref — OtherTimeField registers its label-input focus function here. */
  focusOtherLabelRef: RefObject<(() => void) | null>
}

const TimerStateContext = createContext<TimerStateContextValue>({
  setTimerRunning: () => {},
  stopOtherTimers: () => {},
  pauseFunctionsRef: { current: {} },
  startFunctionsRef: { current: {} },
  runningTimers: {},
  showOtherTime: false,
  setShowOtherTime: () => {},
  openTimePanelForOtherRef: { current: null },
  focusOtherLabelRef: { current: null },
})

export const TimerStateProvider = ({ children }: { children: ReactNode }) => {
  const [runningTimers, setRunningTimers] = useState<Partial<Record<string, boolean>>>({})
  const pauseFunctionsRef = useRef<Record<string, () => void>>({})
  const startFunctionsRef = useRef<Record<string, () => void>>({})
  const [showOtherTime, setShowOtherTime] = useState(false)
  const openTimePanelForOtherRef = useRef<(() => void) | null>(null)
  const focusOtherLabelRef = useRef<(() => void) | null>(null)

  const setTimerRunning = useCallback((fieldName: string, isRunning: boolean) => {
    setRunningTimers(prev => {
      if (!!prev[fieldName] === isRunning) return prev
      return { ...prev, [fieldName]: isRunning }
    })
  }, [])

  // Pause + flush every other timer. Each pause fn is a no-op when its timer
  // isn't running, so this safely stops only the active ones.
  const stopOtherTimers = useCallback((exceptFieldName: string) => {
    Object.entries(pauseFunctionsRef.current).forEach(([fieldName, pause]) => {
      if (fieldName !== exceptFieldName) pause()
    })
  }, [])

  return (
    <TimerStateContext.Provider
      value={{
        setTimerRunning,
        stopOtherTimers,
        pauseFunctionsRef,
        startFunctionsRef,
        runningTimers,
        showOtherTime,
        setShowOtherTime,
        openTimePanelForOtherRef,
        focusOtherLabelRef,
      }}>
      {children}
    </TimerStateContext.Provider>
  )
}

export const useTimerState = () => useContext(TimerStateContext)
